/**
 * Début de séance : composition de la liste figée des cartes de la séance (spécification §5,
 * « Déroulement d'une séance »).
 */
import { State, type EtatFsrs, type Planificateur } from '../fsrs/planificateur';
import { seuilAlerteEffectif, type ReglagesPaquet } from '../paquets/reglages';
import { melanger, type Aleatoire } from './aleatoire';
import { debutJournee, finJournee } from './journee';

/** Carte de l'arborescence d'un paquet principal, telle que la séance en a besoin. */
export interface CarteDuPaquet {
  id: string;
  carteMereId: string;
  /** Le mode de la carte mère reporte ses cartes filles au lendemain (une par jour). */
  reportFilles: boolean;
  /** Date d'ajout de la carte. */
  creeLe: Date;
  /** Rang de la carte dans sa carte mère : départage les cartes ajoutées ensemble. */
  rang: number;
  suspendue: boolean;
  fsrs: EtatFsrs;
}

/** Révision déjà enregistrée (journal), utile aux limites quotidiennes et au report. */
export interface RevisionPassee {
  carteId: string;
  reviseLe: Date;
  /** État de la carte avant la révision. */
  etatAvant: State;
}

export interface DebutSeance {
  /** Toutes les cartes de l'arborescence du paquet principal, suspendues comprises. */
  cartes: readonly CarteDuPaquet[];
  /** Révisions des cartes du paquet, au moins depuis le début de la journée du paquet. */
  revisions: readonly RevisionPassee[];
  reglages: ReglagesPaquet;
  planificateur: Planificateur;
  maintenant: Date;
  aleatoire: Aleatoire;
}

/** Ordre d'ajout : les plus anciennes d'abord. */
export function comparerOrdreAjout(a: CarteDuPaquet, b: CarteDuPaquet): number {
  return (
    a.creeLe.getTime() - b.creeLe.getTime() ||
    a.rang - b.rang ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/** Carte en apprentissage (étapes d'apprentissage ou de réapprentissage). */
export function estEnApprentissage(etat: EtatFsrs): boolean {
  return etat.state === State.Learning || etat.state === State.Relearning;
}

/**
 * Apprentissage interday : la prochaine étape tombe après la fin de la journée du paquet
 * où la carte a été revue (glossaire).
 */
export function estApprentissageInterday(etat: EtatFsrs, heureDebut: number): boolean {
  return (
    estEnApprentissage(etat) &&
    etat.last_review !== undefined &&
    debutJournee(etat.due, heureDebut).getTime() >
      debutJournee(etat.last_review, heureDebut).getTime()
  );
}

/**
 * Une carte déjà vue est-elle due à cet instant ?
 * - acquise ou en apprentissage interday : dès le début de la journée du paquet où elle
 *   revient (les dues se comptent par journée du paquet, ADR 0010) ;
 * - en apprentissage intraday : quand son heure est arrivée.
 * Une nouvelle carte n'est jamais « due » : elle entre par la limite quotidienne.
 */
export function estDue(etat: EtatFsrs, maintenant: Date, heureDebut: number): boolean {
  if (etat.state === State.New) return false;
  if (etat.state === State.Review || estApprentissageInterday(etat, heureDebut)) {
    return etat.due.getTime() < finJournee(maintenant, heureDebut).getTime();
  }
  return etat.due.getTime() <= maintenant.getTime();
}

/**
 * Report des cartes filles au lendemain : une seule par carte mère et par journée, pour les
 * modes où une carte fille peut dévoiler la réponse d'une autre. Une carte fille déjà revue
 * aujourd'hui reste la seule autorisée.
 */
function creerReport(cartes: readonly CarteDuPaquet[], revisionsDuJour: readonly RevisionPassee[]) {
  const parId = new Map(cartes.map((carte) => [carte.id, carte]));
  const revuesAujourdhui = new Map<string, Set<string>>();
  for (const revision of revisionsDuJour) {
    const carte = parId.get(revision.carteId);
    if (carte === undefined || !carte.reportFilles) continue;
    const revues = revuesAujourdhui.get(carte.carteMereId) ?? new Set<string>();
    revues.add(carte.id);
    revuesAujourdhui.set(carte.carteMereId, revues);
  }
  const retenues = new Map<string, string>();
  return {
    autorisee(carte: CarteDuPaquet): boolean {
      if (!carte.reportFilles) return true;
      const revues = revuesAujourdhui.get(carte.carteMereId);
      if (revues !== undefined) return revues.has(carte.id);
      const retenue = retenues.get(carte.carteMereId);
      return retenue === undefined || retenue === carte.id;
    },
    retenir(carte: CarteDuPaquet): void {
      if (carte.reportFilles && !revuesAujourdhui.has(carte.carteMereId)) {
        retenues.set(carte.carteMereId, carte.id);
      }
    },
  };
}

/** Compteur d'une limite quotidienne ; `Infinity` pour une catégorie sans limite. */
interface Limite {
  restantes: number;
}

/**
 * Liste des cartes de la séance, dans l'ordre de présentation de la phase 1. Figée au début
 * de séance : une carte qui n'y est pas n'entrera pas dans la séance.
 */
export function composerListe(debut: DebutSeance): CarteDuPaquet[] {
  const { reglages, maintenant, planificateur, aleatoire } = debut;
  const heure = reglages.heureDebutJournee;
  const actives = debut.cartes.filter((carte) => !carte.suspendue);

  if (reglages.ordreCartes === 'toutes_chronologique') {
    return [...actives].sort(comparerOrdreAjout);
  }

  const debutDuJour = debutJournee(maintenant, heure).getTime();
  const revisionsDuJour = debut.revisions.filter(
    (revision) => revision.reviseLe.getTime() >= debutDuJour,
  );
  const compter = (etat: State) =>
    revisionsDuJour.filter((revision) => revision.etatAvant === etat).length;
  const nouvelles: Limite = {
    restantes: Math.max(0, reglages.nouvellesCartesParJour - compter(State.New)),
  };
  const acquises: Limite = {
    restantes: Math.max(0, reglages.revisionsMaxParJour - compter(State.Review)),
  };
  const sansLimite: Limite = { restantes: Infinity };

  const report = creerReport(debut.cartes, revisionsDuJour);
  const selection: CarteDuPaquet[] = [];
  const prendre = (carte: CarteDuPaquet, limite: Limite) => {
    if (limite.restantes <= 0 || !report.autorisee(carte)) return;
    report.retenir(carte);
    selection.push(carte);
    limite.restantes -= 1;
  };

  if (reglages.ordreCartes === 'probabilite_rappel') {
    // Phase 1 : cartes sous le seuil d'alerte, nouvelles exclues, les plus près d'être
    // oubliées d'abord. Les cartes acquises restent soumises aux révisions maximum par jour.
    const seuil = seuilAlerteEffectif(reglages);
    actives
      .filter((carte) => carte.fsrs.state !== State.New)
      .map((carte) => ({ carte, rappel: planificateur.probabiliteRappel(carte.fsrs, maintenant) }))
      .filter(({ rappel }) => rappel < seuil)
      .sort((a, b) => a.rappel - b.rappel || comparerOrdreAjout(a.carte, b.carte))
      .forEach(({ carte }) =>
        prendre(carte, carte.fsrs.state === State.Review ? acquises : sansLimite),
      );
    return selection;
  }

  // Ordre chronologique ou au hasard : les cartes sont piochées dans cet ordre, puis
  // présentées dans ce même ordre. Les cartes en apprentissage ne sont pas limitées.
  const ordonner = (cartes: CarteDuPaquet[]) =>
    reglages.ordreCartes === 'chronologique'
      ? cartes.sort(comparerOrdreAjout)
      : melanger(cartes, aleatoire);
  const dues = actives.filter((carte) => estDue(carte.fsrs, maintenant, heure));
  ordonner(dues.filter((carte) => estEnApprentissage(carte.fsrs))).forEach((carte) =>
    prendre(carte, sansLimite),
  );
  ordonner(dues.filter((carte) => carte.fsrs.state === State.Review)).forEach((carte) =>
    prendre(carte, acquises),
  );
  ordonner(actives.filter((carte) => carte.fsrs.state === State.New)).forEach((carte) =>
    prendre(carte, nouvelles),
  );
  return ordonner(selection);
}
